import { useEffect, useMemo, useState } from "react";
import { Modal } from "../../../../shared/ui";
import { ArbolOrganizacion } from "./ArbolOrganizacion";
import { ResumenAccesos } from "./ResumenAccesos";
import { SelectorConfiguracion } from "./SelectorConfiguracion";
import { ConfiguracionSimple } from "./ConfiguracionSimple";
import { IconoBuscar } from "./iconos";
import { filtrarArbol, sedesDeArbol } from "../../utils/arbolOrganizacion";

interface AccesosOrganizacionModalProps {
    acc: any;
}

// Buscador y acciones masivas sobre lo visible del árbol.
const BarraArbol = ({ busqueda, setBusqueda, visibles, acc, todoContraido, alternarTodo }: any) => (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-border-b tw-border-gray-200 tw-bg-white tw-p-3">
        <div className="tw-relative tw-flex-1 tw-min-w-[220px]">
            <span className="tw-pointer-events-none tw-absolute tw-left-2.5 tw-top-1/2 -tw-translate-y-1/2 tw-text-gray-400"><IconoBuscar /></span>
            <input type="search" aria-label="Buscar compañía, sucursal o sede" placeholder="Buscar compañía, sucursal o sede..."
                className="form-control form-control-sm tw-pl-8" style={{ textTransform: "none" }}
                value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
        </div>
        <button type="button" className="btn btn-sm btn-primario" onClick={() => acc.marcar(visibles, true)}>
            {busqueda ? "Marcar resultados" : "Marcar todas"}
        </button>
        <button type="button" className="btn btn-sm btn-secundario" onClick={() => acc.marcar(visibles, false)}>
            {busqueda ? "Quitar resultados" : "Quitar todas"}
        </button>
        <button type="button" className="btn btn-sm btn-secundario" onClick={alternarTodo}>
            {todoContraido ? "Expandir todo" : "Contraer todo"}
        </button>
    </div>
)

// Árbol completo con buscador y resumen lateral (configuración por organización).
const PanelOrganizacion = ({ acc, busqueda, setBusqueda, visible, colapsados, alternar, todoContraido, alternarTodo }: any) => (
    <div className="tw-mt-4 tw-grid tw-grid-cols-1 lg:tw-grid-cols-[minmax(0,1fr)_340px] tw-gap-4">
        <section className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-gray-200 tw-bg-gray-50/60">
            <BarraArbol busqueda={busqueda} setBusqueda={setBusqueda} visibles={sedesDeArbol(visible)} acc={acc}
                todoContraido={todoContraido} alternarTodo={alternarTodo} />
            <div className="tw-max-h-[52vh] tw-overflow-y-auto">
                {acc.cargandoOrg
                    ? <p className="tw-p-6 tw-text-center tw-text-sm tw-text-gray-500">Cargando compañías y sedes...</p>
                    : <ArbolOrganizacion arbol={visible} borrador={acc.borrador} marcar={acc.marcar}
                        colapsados={busqueda ? new Set() : colapsados} alternar={alternar} companiaUsuario={acc.compania} />}
            </div>
        </section>
        <aside><ResumenAccesos arbol={acc.arbol} borrador={acc.borrador} marcar={acc.marcar} principal={acc.principal} setPrincipal={acc.setPrincipal} /></aside>
    </div>
)

// Modal "Accesos por organización": tipo de configuración (simple o por
// organización) y, según el tipo, los selects de siempre o el árbol completo.
export const AccesosOrganizacionModal = (props: AccesosOrganizacionModalProps) => {
    const { acc } = props
    const [busqueda, setBusqueda] = useState("")
    const [colapsados, setColapsados] = useState<Set<string>>(new Set())
    const [intentoVacio, setIntentoVacio] = useState(false)
    const visible = useMemo(() => filtrarArbol(acc.arbol, busqueda), [acc.arbol, busqueda])
    const todoContraido = acc.arbol.length > 0 && acc.arbol.every((c: any) => colapsados.has(`c:${c.codigo}`))

    const alternar = (clave: string) => {
        const nuevo = new Set(colapsados)
        if (nuevo.has(clave)) nuevo.delete(clave); else nuevo.add(clave)
        setColapsados(nuevo)
    }
    const alternarTodo = () => setColapsados(todoContraido ? new Set() : new Set(acc.arbol.map((c: any) => `c:${c.codigo}`)))
    // Al abrir: desplegada la compañía del usuario y las que ya tienen sedes marcadas;
    // las que no se usan, contraídas.
    useEffect(() => {
        if (!acc.abierto || acc.cargandoOrg) return
        const enUso = (c: any) => c.codigo === acc.compania
            || c.sucursales.some((s: any) => s.sedes.some((x: any) => acc.borrador.has(x.codigo)))
        setColapsados(new Set(acc.arbol.filter((c: any) => !enUso(c)).map((c: any) => `c:${c.codigo}`)))
        // solo al abrir o al terminar de cargar el árbol; luego manda el usuario
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [acc.abierto, acc.cargandoOrg])

    const aplicar = () => setIntentoVacio(!acc.aplicar())

    return (
        <Modal open={acc.abierto} size="2xl" overlayClassName="tw-bg-black/75" showClose dismissable={false} title="Accesos por organización"
            onOpenChange={(o) => { if (!o) acc.cancelar() }}>
            <p className="tw-text-sm tw-text-gray-500 tw-mb-3">Elija el tipo de configuración y las sedes a las que tendrá acceso el usuario.</p>
            <SelectorConfiguracion acc={acc} />
            {acc.modoBorrador === "grupo"
                ? <ConfiguracionSimple acc={acc} />
                : <PanelOrganizacion acc={acc} busqueda={busqueda} setBusqueda={setBusqueda} visible={visible} colapsados={colapsados}
                    alternar={alternar} todoContraido={todoContraido} alternarTodo={alternarTodo} />}
            <div className="tw-sticky tw-bottom-0 -tw-mx-3 sm:-tw-mx-4 -tw-mb-3 sm:-tw-mb-4 tw-mt-4 tw-flex tw-flex-wrap tw-items-center tw-gap-3 tw-border-t tw-border-gray-200 tw-bg-white tw-px-4 tw-py-3">
                <span className="tw-text-sm tw-text-gray-600"><strong>{acc.totalBorrador}</strong> sede(s) · {acc.modoBorrador === "grupo" ? "Simple" : "Por organización"}</span>
                {intentoVacio && acc.totalBorrador === 0 && <span role="alert" className="tw-text-sm tw-text-red-600">Seleccione al menos una sede</span>}
                <div className="tw-ml-auto tw-flex tw-gap-2">
                    <button type="button" className="btn btn-sm btn-secundario" onClick={acc.cancelar}>Cancelar</button>
                    <button type="button" className="btn btn-sm btn-primario" onClick={aplicar}>Aplicar cambios</button>
                </div>
            </div>
        </Modal>
    )
}
